import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { productKeys } from './keys';

import type { TypeGetSalesChannelParams, TypeGetSalesChannelResponse, TypeMappingProduct } from '~/types/product.types';

/**
 * [상품관리] 상품 판매처 매핑 리스트 조회 API
 */
export const fetchSalesChannel = async (params: TypeGetSalesChannelParams): Promise<TypeGetSalesChannelResponse> => {
  const endpoint = '/api-lip/v1/third-parties/sales-channel';
  const { data } = await axiosInstance.get<TypeGetSalesChannelResponse>(endpoint, { params });
  return data;
};

type UseGetSalesChannelQueryOptions = {
  params: TypeGetSalesChannelParams;
  enabled?: boolean;
  showGlobalDimOverlay?: boolean;
};

/**
 * [상품관리] 상품 판매처 매핑 리스트 조회 Query Hook
 */
export const useGetSalesChannelQuery = ({
  params,
  enabled = true,
  showGlobalDimOverlay = true,
}: UseGetSalesChannelQueryOptions) => {
  return useQuery({
    queryKey: productKeys.salesChannel(params),
    queryFn: () => fetchSalesChannel(params),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
    meta: { showGlobalDimOverlay },
  });
};

const mockSalesChannel = (index: number, overrides: Partial<TypeMappingProduct> = {}): TypeMappingProduct => ({
  skuId: 2000 + index,
  skuCode: `M000002076${String(index).padStart(2, '0')}`,
  skuName: `(센터명)매핑상품${index}`,
  createdDate: '2026-05-12',
  consignorCode: 'CS000001',
  consignorName: 'e2e-owner',
  salesChannelId: 3000 + index,
  salesChannelCode: 'SC001',
  salesChannelName: '테스트판매처',
  salesChannelSkuCode: `SC-SKU-${index}`,
  salesChannelSkuName: `판매처상품${index}`,
  skuActiveType: '운영 상품',
  ...overrides,
});

const salesChannelNormalResponse: TypeGetSalesChannelResponse = {
  data: [
    mockSalesChannel(1),
    mockSalesChannel(2, { salesChannelName: '두번째판매처', salesChannelCode: 'SC002' }),
    // 판매처 미등록 — 판매처 등록 버튼 노출, 상세보기 비활성
    mockSalesChannel(3, {
      salesChannelId: 0,
      salesChannelCode: '',
      salesChannelName: '',
      salesChannelSkuCode: '',
      salesChannelSkuName: '',
    }),
    mockSalesChannel(4, { skuActiveType: '미운영 상품', createdDate: '2026-05-19' }),
  ],
  pagination: { listView: 50, listTotal: 4, cursor: 0 },
};

registerMockAPI<TypeGetSalesChannelResponse>({
  method: 'get',
  endpoint: '/api-lip/v1/third-parties/sales-channel',
  label: '상품/상품판매처매핑/판매처목록',
  scenarios: [
    { name: '정상 응답', response: salesChannelNormalResponse },
    { name: '검색 결과 0건', response: { data: [], pagination: { listView: 50, listTotal: 0, cursor: 0 } } },
  ],
});
